import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { IDashcam, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription, map, retry, throwError, timer } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { ICONO_DASHCAM } from '../../../../../auxiliares/iconos/iconos';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DashcamsService } from '../../../../../auxiliares/servicios/http/dashcams.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { BOTONES } from '../../../../../componentes/botones';
import { ListadoLogsCrudosComponent } from '../../../logs/listado-logs-crudos/listado-logs-crudos.component';
import { EventosGenericosModule } from '../../../eventos-genericos/module';
import { ListadoReportesGenericosComponent } from '../../../../standalone/listado-reportes-genericos/listado-reportes-genericos.component';
import {
  CrearEditarDashcamComponent,
  ICrearEditarDashcamData,
} from '../crear-editar-dashcam/crear-editar-dashcam.component';
import { DetallesDashcamInfoComponent } from '../detalles-dashcam-info/detalles-dashcam-info.component';

type Pestana = 'info' | 'alarmas' | 'reportes' | 'logs';

/**
 * Detalle de dashcam, con el marco del detalle de tracker. Todavía sin
 * "En vivo" ni "Grabaciones" (todavía no hay servidor de video). Tampoco
 * "Códigos de eventos": la lista de códigos personalizados sólo sabe crear
 * para alarmas y activos; los códigos de la dashcam se configuran en su
 * modelo (Configuración → Códigos/Modelos Dashcams).
 */
@Component({
  selector: 'app-detalles-dashcam',
  standalone: true,
  imports: [
    AuxiliaresModule,
    BOTONES,
    EventosGenericosModule,
    ListadoReportesGenericosComponent,
    ListadoLogsCrudosComponent,
    DetallesDashcamInfoComponent,
  ],
  templateUrl: './detalles-dashcam.component.html',
  styleUrl: '../../../../../componentes/detalle/detalle-pagina.scss',
})
export class DetallesDashcamComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  service = inject(DashcamsService);
  private lista2 = inject(Lista2Service);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);

  public readonly icono = ICONO_DASHCAM;
  public idDashcam = '';
  public dashcam?: IDashcam;
  public loading = true;
  public selectedTab = 0;
  public verLogs = this.service.puedeVerLogsCrudos();
  private dashcam$?: Subscription;
  private params$?: Subscription;
  private queryParams$?: Subscription;

  public get titulo(): string {
    return DashcamsService.nombre(this.dashcam);
  }

  /** El nombre, si el título es la identificación. */
  public get subtitulo(): string | undefined {
    const d = this.dashcam;
    return d?.identificacion && d?.nombre && d.nombre !== d.identificacion
      ? d.nombre
      : undefined;
  }

  public get cliente(): string | undefined {
    if (HelperService.getTipoCliente() === 'Final') return undefined;
    return this.dashcam?.cliente?.nombre || undefined;
  }

  /** Las pestañas en orden, por nombre. Tiene que coincidir con la plantilla. */
  private pestanas(): Pestana[] {
    const pestanas: Pestana[] = ['info', 'alarmas', 'reportes'];
    if (this.verLogs) pestanas.push('logs');
    return pestanas;
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice. */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab as Pestana);
    return i >= 0 ? i : null;
  }

  /**
   * GET /:id no popula: se pide por el listado con populate, igual que el
   * detalle de tracker.
   */
  private listarDashcam(): void {
    const populate: IPopulate[] = [
      { path: 'cliente', select: 'nombre' },
      { path: 'modelo', select: 'marca modelo protocoloDashcam' },
      { path: 'activo', select: 'identificacion categoria' },
      { path: 'sim1' },
      { path: 'sim2' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ _id: this.idDashcam }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      limit: 1,
    };
    this.dashcam$?.unsubscribe();
    this.dashcam$ = this.lista2
      .dashcams(query, 30)
      .pipe(
        map((listado) => listado.datos?.[0]),
        // Un request que falla terminaba el refresco para siempre. Si todavía
        // no se cargó nunca, el error sigue de largo.
        retry({
          delay: (error) =>
            this.dashcam ? timer(30000) : throwError(() => error),
        }),
      )
      .subscribe({
        next: (dashcam) => {
          this.dashcam = dashcam;
          this.loading = false;
        },
        error: (error) => {
          this.helper.notifError(error);
          this.loading = false;
        },
      });
  }

  public editar(): void {
    if (!this.dashcam) return;
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: { edit: this.dashcam } },
      )
      .afterClosed()
      .subscribe((res) => {
        // Refrescar ya: el polling es cada 30 s
        if (res) this.listarDashcam();
      });
  }

  /** Angular reutiliza el componente al ir de una dashcam a otra. */
  private cargar(id: string | null): void {
    this.loading = true;
    this.dashcam = undefined;
    this.idDashcam = id ?? '';
    this.listarDashcam();
  }

  ngOnInit(): void {
    this.params$ = this.activatedRoute.paramMap.subscribe((params) => {
      this.cargar(params.get('id'));
    });
    this.queryParams$ = this.activatedRoute.queryParams.subscribe((qp) => {
      const indice = qp['tab'] ? this.indicePestana(qp['tab']) : null;
      if (indice !== null) this.selectedTab = indice;
    });
  }

  ngOnDestroy(): void {
    this.dashcam$?.unsubscribe();
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
